import { useCallback, useRef, useState, type ReactNode } from "react"
import { useEscape } from "./useEscape"

export interface ConfirmRequest {
	/** Сам вопрос — одной строкой, жирным. */
	title: ReactNode
	/** Что именно произойдёт и что нельзя будет отменить. Обычно строка, но
	 *  тип честный: сюда кладут и разметку — например список групп, которые не
	 *  попадут в каталог (`PdfScreen.tsx#PreflightSummary`). Поэтому обёртка
	 *  ниже — `<div>`, а не `<p>`: список внутри абзаца браузер выбрасывает
	 *  наружу, ломая и разметку, и стили. */
	text?: ReactNode
	/** Подпись подтверждающей кнопки. По умолчанию «Да». */
	confirm?: string
	/** Действие разрушающее — кнопка красная. */
	danger?: boolean
}

/**
 * Подтверждение опасного действия.
 *
 * Одно окно на всё приложение вместо трёх самодельных панелей и полудюжины
 * `window.confirm` с многострочными текстами. Ответ приходит промисом, поэтому
 * вызывающий код не меняет формы:
 *
 * ```ts
 * if (!(await ask({ title: "Перезалить таблицы?" }))) return
 * ```
 *
 * Само окно рисует вызывающий: `{dialog}` в его разметке. Esc — отмена, как и
 * у всех прочих перекрывающих слоёв (`useEscape`).
 *
 * Единственное исключение, где остался браузерный `confirm`, — предупреждение
 * о несохранённых правках (`hooks/useUnsaved`): там ответ нужен синхронно,
 * прямо в обработчике смены языка.
 */
export function useConfirm(): {
	ask: (request: ConfirmRequest) => Promise<boolean>
	dialog: ReactNode
} {
	const [request, setRequest] = useState<ConfirmRequest | null>(null)
	const answer = useRef<((ok: boolean) => void) | null>(null)

	const close = useCallback((ok: boolean) => {
		setRequest(null)
		answer.current?.(ok)
		answer.current = null
	}, [])

	const ask = useCallback((next: ConfirmRequest) => {
		// предыдущий вопрос, если он ещё висит, считаем отменённым
		answer.current?.(false)
		setRequest(next)
		return new Promise<boolean>(resolve => {
			answer.current = resolve
		})
	}, [])

	useEscape(request !== null, () => close(false))

	const dialog = request ? (
		<div className="confirm" role="alertdialog" aria-modal="true">
			<div>
				<b>{request.title}</b>
				{request.text && <div className="muted confirm__text">{request.text}</div>}
				<p className="muted confirm__esc">Esc — отмена.</p>
			</div>
			<div className="confirm__actions">
				<button className="btn" onClick={() => close(false)}>
					Отмена
				</button>
				<button
					className={"btn " + (request.danger ? "btn--danger" : "btn--primary")}
					autoFocus
					onClick={() => close(true)}
				>
					{request.confirm ?? "Да"}
				</button>
			</div>
		</div>
	) : null

	return { ask, dialog }
}
